import { ComponentProps, ReactNode, useState } from "react";
import { Info } from "lucide-react";
import t from "@/lang/lang";
import {
  PricesUsed,
  priceUsedFromSelectValue,
  priceUsedSelectItems,
  priceUsedToSelectValue,
} from "../types/price-used";
import { serviceVariationLabel } from "@/services/backend/omni/servicevariation/types/service-variation";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { DropdownMenuItem } from "@/shared/components/ui/dropdown-menu";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
} from "@/shared/components/ui/field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { CommonDialogFooter } from "@/shared/components/dialog/dialog-common";
import { FieldErrors } from "@/shared/components/field/field";
import {
  DecimalNumberInputField,
  TextInputField,
} from "@/shared/components/field/input-field";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { DocumentBase } from "@/services/backend/documents/types/base";
import { ServiceObjectReference } from "@/services/backend/documents/types/service-object-references-base";
import { ServiceObjectName } from "@/services/backend/customers/components/service-object-name";
import {
  Position,
  PositionPatch,
  PositionSubscriptionIntervalSelectItems,
  subscriptionIntervalFromSelectValue,
  subscriptionIntervalToSelectValue,
} from "../types/position";
import { isSumKind, PositionKind } from "../types/position-kind";
import { PositionUnitSelectItems } from "../types/position-unit";
import { PositionCategorySelectItems } from "../types/position-category";
import { DataSheetsField } from "@/services/backend/omni/datasheet/components/data-sheets-field";

const positionKindLabels: Record<PositionKind, string> = {
  [PositionKind.Default]: t("Position"),
  [PositionKind.Dynamic]: t("Dynamisch"),
  [PositionKind.Text]: t("Text"),
  [PositionKind.GroupTitle]: t("Gruppentitel"),
  [PositionKind.AreaTitle]: t("Bereichstitel"),
  [PositionKind.PriceModifierRelative]: t("Rabatt (%)"),
  [PositionKind.PriceModifierAbsolute]: t("Rabatt (€)"),
  [PositionKind.SubTotal]: t("Zwischensumme"),
  [PositionKind.Total]: t("Gesamtpreis"),
};

/**
 * hasPrice tells whether a position of kind carries prices of its own.
 * The backend resets the prices of the other kinds, see
 * `Position.calculate`, backend/core/omni/domain/positions/position.go.
 */
function hasPrice(kind: PositionKind): boolean {
  return (
    kind !== PositionKind.Text &&
    kind !== PositionKind.GroupTitle &&
    kind !== PositionKind.AreaTitle &&
    !isSumKind(kind)
  );
}

/**
 * PositionDetailsDialog shows all fields of a position, opened from its
 * actions menu. Unlike the table, it edits a draft and saves it only on
 * confirm: onConfirm saves the patch and resolves to whether it
 * succeeded, the dialog closes on success. The field names are those of
 * the table, so the errors of a failed save show in the dialog.
 */
export function PositionDetailsDialog({
  position,
  parent,
  document,
  groupings,
  editableNumbers,
  pricesUsed,
  disabled,
  fieldNamePrefix,
  onConfirm,
}: {
  position: Position;
  parent?: Position;
  document: DocumentBase;
  groupings: ("group" | "area")[];
  editableNumbers?: boolean;
  pricesUsed: PricesUsed;
  disabled: boolean;
  fieldNamePrefix: string;
  onConfirm: (patch: PositionPatch) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<PositionPatch>({});
  const [isLoading, setIsLoading] = useState(false);

  const priced = hasPrice(position.kind) && !disabled;
  // group and area belong to the parent of a sub-position
  const groupingEditable = (field: "group" | "area") =>
    !disabled && !position.parentId && groupings.includes(field);

  // the draft holds only the fields editable for this position: the
  // backend rejects a group or area patched on a sub-position, even if
  // unchanged
  const newDraft = (): PositionPatch => {
    if (disabled) return {};
    return {
      ...(editableNumbers && { number: position.number }),
      active: position.active,
      category: position.category,
      productNumber: position.productNumber,
      textShort: position.textShort,
      text: position.text,
      serviceObjectId: position.serviceObjectId,
      dataSheetIds: position.dataSheetIds,
      ...(groupingEditable("group") && { group: position.group }),
      ...(groupingEditable("area") && { area: position.area }),
      ...(priced && {
        hours: position.hours,
        hoursRate: position.hoursRate,
        count: position.count,
        unit: position.unit,
        unitPrice: position.unitPrice,
        cycle: position.cycle,
        subscriptionInterval: position.subscriptionInterval,
        priceUsed: position.priceUsed,
      }),
    };
  };

  const openChange = (next: boolean) => {
    if (next) setDraft(newDraft());
    setOpen(next);
  };

  const change = (patch: PositionPatch) =>
    setDraft((current) => ({ ...current, ...patch }));

  const confirm = () => {
    if (isLoading) return;
    setIsLoading(true);
    void onConfirm(draft).then((ok) => {
      setIsLoading(false);
      if (ok) setOpen(false);
    });
  };

  const fieldName = (field: string) =>
    `${fieldNamePrefix}positions.${position.id}.${field}`;
  const value = <K extends keyof PositionPatch>(field: K) =>
    (draft[field] ?? position[field]) as Position[K];

  return (
    <Dialog open={open} onOpenChange={openChange}>
      <DialogTrigger asChild>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            openChange(!open);
          }}
        >
          <Info />
          {t("Details")}
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t("Position")} {position.number}
          </DialogTitle>
          <DialogDescription>
            {positionKindLabels[position.kind] ?? position.kind}
          </DialogDescription>
        </DialogHeader>

        <Section title={t("Allgemein")}>
          <TextInputField
            label={t("Nummer")}
            text={value("number")}
            onTextChange={(number) => change({ number })}
            fieldName={fieldName("number")}
            debounce={false}
            disabled={disabled || !editableNumbers}
          />
          <CheckboxField
            label={t("Aktiv")}
            checked={value("active")}
            onCheckedChange={(active) => change({ active })}
            fieldName={fieldName("active")}
            disabled={disabled}
          />
          <SelectField
            label={t("Kategorie")}
            items={PositionCategorySelectItems}
            value={value("category")}
            onValueChange={(category) => change({ category })}
            fieldName={fieldName("category")}
            disabled={disabled}
          />
          <TextInputField
            label={t("Produktnummer")}
            text={value("productNumber")}
            onTextChange={(productNumber) => change({ productNumber })}
            fieldName={fieldName("productNumber")}
            debounce={false}
            disabled={disabled}
          />
          <TextInputField
            label={t("Kurztext")}
            text={value("textShort")}
            onTextChange={(textShort) => change({ textShort })}
            fieldName={fieldName("textShort")}
            debounce={false}
            disabled={disabled}
            fieldProps={{ className: "sm:col-span-2" }}
          />
          <TextareaField
            label={t("Text")}
            value={value("text")}
            onChange={(e) => change({ text: e.target.value })}
            fieldName={fieldName("text")}
            debounce={false}
            disabled={disabled}
            fieldProps={{ className: "sm:col-span-2" }}
          />
        </Section>

        <Section title={t("Zuordnung")}>
          <SelectField
            label={t("Serviceobjekt")}
            items={[
              { value: NullUUID, label: t("Kein Serviceobjekt") },
              ...(document.serviceObjects ?? []).map(
                (so: ServiceObjectReference) => ({
                  value: so.serviceObjectId,
                  label: <ServiceObjectName id={so.serviceObjectId} />,
                }),
              ),
            ]}
            value={value("serviceObjectId") || NullUUID}
            onValueChange={(serviceObjectId) => change({ serviceObjectId })}
            fieldName={fieldName("serviceObjectId")}
            disabled={disabled}
          />
          <TextInputField
            label={t("Übergeordnete Position")}
            text={parent?.number ?? ""}
            onTextChange={() => null}
            debounce={false}
            disabled
          />
          <TextInputField
            label={t("Gruppe")}
            text={value("group")}
            onTextChange={(group) => change({ group })}
            fieldName={fieldName("group")}
            debounce={false}
            disabled={!groupingEditable("group")}
          />
          <TextInputField
            label={t("Bereich")}
            text={value("area")}
            onTextChange={(area) => change({ area })}
            fieldName={fieldName("area")}
            debounce={false}
            disabled={!groupingEditable("area")}
          />
          <TextInputField
            label={t("Leistungsart")}
            text={position.serviceKind}
            onTextChange={() => null}
            debounce={false}
            disabled
          />
          <TextInputField
            label={t("Variation")}
            text={serviceVariationLabel(position.serviceVariation)}
            onTextChange={() => null}
            fieldName={fieldName("serviceVariation")}
            debounce={false}
            disabled
          />
          <TextInputField
            label={t("Reihenfolge")}
            text={String(position.order)}
            onTextChange={() => null}
            debounce={false}
            disabled
          />
          <TextInputField
            label={t("ID")}
            text={position.id}
            onTextChange={() => null}
            debounce={false}
            disabled
            fieldProps={{ className: "sm:col-span-2" }}
          />
          {document.companyId && document.serviceKind && (
            <DataSheetsField
              companyId={document.companyId}
              serviceKind={document.serviceKind}
              value={value("dataSheetIds") ?? []}
              onValueChange={(dataSheetIds) => change({ dataSheetIds })}
              fieldName={fieldName("dataSheetIds")}
              disabled={disabled}
            />
          )}
        </Section>

        <Section title={t("Preis")}>
          <DecimalNumberInputField
            label={t("Stunden")}
            value={value("hours")}
            onValueChange={(hours) => change({ hours })}
            fieldName={fieldName("hours")}
            debounce={false}
            disabled={!priced}
          />
          <DecimalNumberInputField
            label={t("Stundensatz")}
            value={value("hoursRate")}
            onValueChange={(hoursRate) => change({ hoursRate })}
            precision={2}
            fieldName={fieldName("hoursRate")}
            debounce={false}
            disabled={!priced}
          />
          <DecimalNumberInputField
            label={t("Anzahl")}
            value={value("count")}
            onValueChange={(count) => change({ count })}
            fieldName={fieldName("count")}
            debounce={false}
            disabled={!priced}
          />
          <SelectField
            label={t("Einheit")}
            items={PositionUnitSelectItems}
            value={value("unit")}
            onValueChange={(unit) => change({ unit })}
            fieldName={fieldName("unit")}
            disabled={!priced}
          />
          <DecimalNumberInputField
            label={t("Preis / Einheit")}
            value={value("unitPrice")}
            onValueChange={(unitPrice) => change({ unitPrice })}
            precision={2}
            fieldName={fieldName("unitPrice")}
            debounce={false}
            disabled={!priced}
          />
          <DecimalNumberInputField
            label={t("Grundpreis")}
            value={position.basePrice}
            onValueChange={() => null}
            precision={2}
            debounce={false}
            disabled
          />
          <TextInputField
            label={t("Rabatte / Zuschläge")}
            text={String(position.priceModifiers.length)}
            onTextChange={() => null}
            debounce={false}
            disabled
          />
          <DecimalNumberInputField
            label={t("Endpreis")}
            value={position.finalPrice}
            onValueChange={() => null}
            precision={2}
            debounce={false}
            disabled
          />
          {pricesUsed.allowed.length > 1 && (
            <SelectField
              label={t("Preis im Dokument")}
              items={priceUsedSelectItems(pricesUsed)}
              value={priceUsedToSelectValue(value("priceUsed"))}
              onValueChange={(v) =>
                change({ priceUsed: priceUsedFromSelectValue(v) })
              }
              fieldName={fieldName("priceUsed")}
              disabled={!priced}
            />
          )}
        </Section>

        <Section title={t("Abo")}>
          <DecimalNumberInputField
            label={t("Turnus (Wo.)")}
            value={value("cycle")}
            onValueChange={(cycle) => change({ cycle })}
            precision={0}
            fieldName={fieldName("cycle")}
            debounce={false}
            disabled={!priced}
          />
          <SelectField
            label={t("Abo-Intervall")}
            items={PositionSubscriptionIntervalSelectItems}
            value={subscriptionIntervalToSelectValue(
              value("subscriptionInterval"),
            )}
            onValueChange={(v) =>
              change({
                subscriptionInterval: subscriptionIntervalFromSelectValue(v),
              })
            }
            fieldName={fieldName("subscriptionInterval")}
            disabled={!priced}
          />
          <DecimalNumberInputField
            label={t("Abo-Preis")}
            value={position.subscriptionPrice}
            onValueChange={() => null}
            precision={2}
            debounce={false}
            disabled
          />
        </Section>

        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={confirm}
          actionLabel={t("Übernehmen")}
          disabled={disabled}
        />
      </DialogContent>
    </Dialog>
  );
}

/** Section titles a group of fields of a details dialog. */
export function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-4 sm:grid-cols-2">
      <h3 className="text-sm font-semibold sm:col-span-2">{title}</h3>
      {children}
    </section>
  );
}

/**
 * SelectField selects one of items, without debouncing. It shows and
 * resets the errors of fieldName.
 */
export function SelectField({
  label,
  items,
  fieldName,
  onValueChange,
  ...props
}: ComponentProps<typeof Select> & {
  label: string;
  items: { value: string; label: ReactNode }[];
  fieldName: string;
}) {
  const { errors, invalid, reset } = useFieldErrors(fieldName);

  return (
    <Field data-invalid={invalid || undefined}>
      <FieldLabel>{label}</FieldLabel>
      <Select
        {...props}
        onValueChange={(v) => {
          reset();
          onValueChange?.(v);
        }}
      >
        <SelectTrigger aria-invalid={invalid || undefined} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {items.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      <FieldErrors errors={errors} />
    </Field>
  );
}

/**
 * CheckboxField is a labelled checkbox with an optional description. It
 * shows and resets the errors of fieldName.
 */
export function CheckboxField({
  label,
  description,
  checked,
  onCheckedChange,
  fieldName,
  disabled,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  fieldName: string;
  disabled?: boolean;
}) {
  const { errors, invalid, reset } = useFieldErrors(fieldName);

  return (
    <Field data-invalid={invalid || undefined} orientation="horizontal">
      <Checkbox
        id={fieldName}
        checked={checked}
        onCheckedChange={(v) => {
          reset();
          onCheckedChange(v === true);
        }}
        aria-invalid={invalid || undefined}
        disabled={disabled}
      />
      <FieldContent>
        <FieldLabel htmlFor={fieldName}>{label}</FieldLabel>
        {description && <FieldDescription>{description}</FieldDescription>}
        <FieldErrors errors={errors} />
      </FieldContent>
    </Field>
  );
}
